import React, {MouseEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Map} from 'immutable';
import cn from 'classnames';

import Text from '@material-ui/core/Typography';

import {makeStyles} from '@components/@theme';
import AddButton from '@components/round-icon-btn';
import {replaceAt} from 'utils/replaceAt';

import {ALLOWED_FIELDS, DEFAULT_OPTIONS, DEFAULT_TEMPLATE} from './templated-message.consts';

import EditableRaw from './editable-raw';
import FieldList from './field-list';
import {getFieldHtml, htmlToTemplate, templateToHtml} from './templated-message.utils';

const DEF_TEMP_VALUES = Map();

let curMoveToPos: number | null = null;

interface TemplatedMessageProps {
  title: any;
  value?: string;
  onChange: (newValue: string) => void;
  templateValues?: Map<ALLOWED_FIELDS, string>;
  textClassname?: string;
  className?: string;
}

export const TemplatedMessage: React.FC<TemplatedMessageProps> = ({
  onChange,
  title,
  value,
  templateValues = DEF_TEMP_VALUES,
  textClassname,
  className,
}) => {
  const [firstInitialization, setFirstInitialization] = useState<boolean>(true);
  useEffect(() => {
    setTimeout(() => {
      setFirstInitialization(false);
    }, 500);
  });

  useEffect(() => {
    if (!value && firstInitialization) {
      onChange(DEFAULT_TEMPLATE);
      setFirstInitialization(false);
    }
  }, [firstInitialization, onChange, value]);

  const getFieldValue = useCallback<(v: ALLOWED_FIELDS) => string>(
    (fieldName: ALLOWED_FIELDS): string => {
      const templateFieldValue = templateValues?.get(fieldName) as string;
      if (templateFieldValue) {
        return templateFieldValue;
      }

      return DEFAULT_OPTIONS.find(el => el.value === fieldName)?.label || fieldName;
    },
    [templateValues]
  );

  const allTextRef = useRef<HTMLDivElement>(null);
  const insertBtnRef = useRef<HTMLButtonElement>(null);

  const [currentField, setCurrentField] = useState<HTMLElement | null>(null);

  const handleClose = useCallback(() => {
    setCurrentField(null);
  }, [setCurrentField]);

  const keysToValues = useCallback(
    (textContent: string): [string, Array<string>] => {
      let res = textContent;
      const keys = Object.values(ALLOWED_FIELDS).map(key =>
        key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      );
      const matches = [...res.matchAll(new RegExp(`{{${keys.join('}}|{{')}}}`, 'g'))];
      const matchRes = matches.map(([pattern]) => {
        const field = pattern.replace('{{', '').replace('}}', '') as ALLOWED_FIELDS;
        const value = getFieldValue(field);
        res = res.replace(pattern, `{{${value}}}`);
        return pattern;
      });
      return [res, matchRes];
    },
    [getFieldValue]
  );
  const valuesToKeys = useCallback(
    (textContent: string, fieldPatterns: Array<string>): string => {
      let res = textContent;
      const values = Object.values(ALLOWED_FIELDS).map(key => {
        const value = getFieldValue(key as ALLOWED_FIELDS);
        return String(value)?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      });
      const matches = [...res.matchAll(new RegExp(`{{${values.join('}}|{{')}}}`, 'g'))];
      matches.forEach(([pattern], index) => {
        res = res.replace(pattern, fieldPatterns[index]);
      });
      return res;
    },
    [getFieldValue]
  );

  const handleChangeValue = useCallback(
    newHtmlValue => {
      const newValue = htmlToTemplate(newHtmlValue);

      onChange(newValue);
    },
    [onChange]
  );

  const [cursorPosition, setCursorPosition] = useState<[number, number]>([-1, -1]);
  const [moveToPos, setMoveToPos] = useState(-1);
  const handleInsertField = useCallback(
    insertion => {
      const position = cursorPosition;
      if (position[0] > -1 && position[1] > -1 && value && value.length > position[1]) {
        // it is required to correctly calculate amount of characters
        const re = new RegExp('&nbsp;', 'g');
        let newValue = value.replace(re, ' ');

        const [keysToValueRes, fields] = keysToValues(newValue);
        newValue = keysToValueRes;
        newValue = replaceAt(newValue, position[0], position[1], insertion);
        newValue = valuesToKeys(newValue, fields);

        if (value !== newValue) {
          onChange(newValue);
        }
      } else {
        onChange(value + insertion);
      }

      const [insertionValue] = keysToValues(insertion);
      const insertionLength = insertionValue.length;
      curMoveToPos = position[0] + insertionLength;
    },
    [cursorPosition, keysToValues, valuesToKeys, onChange, value]
  );

  const handleInsertClick = useCallback(
    evt => {
      setCurrentField(evt.currentTarget);
    },
    [setCurrentField]
  );

  const prepValue = useMemo(() => {
    if (!value) {
      return '';
    }
    const _prepValue = templateToHtml(value, getFieldValue);
    if (typeof curMoveToPos === 'number') {
      setMoveToPos(curMoveToPos);
      setCursorPosition([curMoveToPos, curMoveToPos]);
    }
    return _prepValue;
  }, [getFieldValue, value]);

  const replaceCurrentField = useCallback<(newField?: ALLOWED_FIELDS | null) => void>(
    (newField: ALLOWED_FIELDS | null = null) => {
      let newValue = '';
      if (prepValue && currentField) {
        const index = prepValue.indexOf(`id="${currentField.id}"`);
        if (index !== -1) {
          let preparedReplacement = '';
          if (newField) {
            preparedReplacement = getFieldHtml(currentField.id, newField, getFieldValue);
          }

          const oldFieldHtml = getFieldHtml(
            currentField.id,
            currentField.dataset['field'] as ALLOWED_FIELDS,
            getFieldValue
          );
          newValue = prepValue.replace(oldFieldHtml, preparedReplacement);
        }
      }
      setTimeout(() => {
        if (newValue) {
          handleChangeValue(newValue);
        }
        setCurrentField(null);
      }, 200);
    },
    [currentField, handleChangeValue, getFieldValue, prepValue, setCurrentField]
  );

  const handleChange = useCallback(
    (newHtmlValue: string, newCursorPosition: [number, number]) => {
      curMoveToPos = null;
      if (Array.isArray(newCursorPosition)) {
        let start: number;
        let end: number;
        if (newCursorPosition[0] < newCursorPosition[1]) {
          start = newCursorPosition[0];
          end = newCursorPosition[1];
        } else {
          start = newCursorPosition[1];
          end = newCursorPosition[0];
        }
        setCursorPosition([start, end]);
      }

      handleChangeValue(newHtmlValue);
    },
    [handleChangeValue]
  );

  const handleSelectField = useCallback<(v: ALLOWED_FIELDS) => void>(
    (fieldName: ALLOWED_FIELDS) => {
      if (!currentField?.id) {
        handleInsertField(`{{${fieldName}}}`);
      } else {
        replaceCurrentField(fieldName);
      }
    },
    [currentField, handleInsertField, replaceCurrentField]
  );

  const handleRemoveField = useCallback(() => {
    replaceCurrentField();
  }, [replaceCurrentField]);

  const handleEditableAriaClick = useCallback(
    (evt: MouseEvent) => {
      if (evt.currentTarget === evt.target) {
        curMoveToPos = 0;
        // @NOTE: Infinity means also 0 position here.
        // It must be here, because we should be able initiate setMoveToPos value again to focus on text
        setMoveToPos(s => (s === 0 ? Infinity : 0));
      }
    },
    [setMoveToPos]
  );

  const fieldListStyle = useMemo<{top: number; left: number}>(() => {
    const node: any = currentField ||
      insertBtnRef.current || {
        offsetTop: 0,
        offsetLeft: 0,
        offsetHeight: 0,
        offsetWidth: 0,
      };

    let res: any;
    if (typeof node.getBoundingClientRect === 'function') {
      const rect = node.getBoundingClientRect();
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      res = {
        top: rect.top + scrollTop + node.offsetHeight,
        left: rect.left + scrollLeft - 110 + node.offsetWidth / 2,
      };
    } else {
      res = {
        top: node.offsetTop + node.offsetHeight,
        left: node.offsetLeft - 110 + node.offsetWidth / 2,
      };
    }

    return res;
  }, [currentField, insertBtnRef]);

  const {editableAria, titleStyle, wrapper} = useStyles();
  return (
    <div className={cn(wrapper, className)}>
      <div className={titleStyle}>
        {typeof title === 'string' ? <Text variant="h5">{title}</Text> : title}
        <AddButton ref={insertBtnRef} icon="add_circle" onClick={handleInsertClick}>
          Insert Field
        </AddButton>
      </div>
      <div
        onMouseDown={handleEditableAriaClick}
        ref={allTextRef}
        className={cn(editableAria, textClassname)}>
        <EditableRaw
          setTarget={setCurrentField}
          value={prepValue}
          onChange={handleChange}
          moveToPos={moveToPos}
          shouldBeMoved={typeof curMoveToPos === 'number'}
        />
      </div>

      <FieldList
        style={fieldListStyle}
        key={currentField?.id ?? 'creation'}
        isCreate={!currentField?.id}
        open={Boolean(currentField)}
        onChange={handleSelectField}
        onClose={handleClose}
        onRemove={handleRemoveField}
        options={DEFAULT_OPTIONS}
      />
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  editableAria: {
    border: '0.5px solid #CCCCCC',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
  },
  titleStyle: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  wrapper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.25)',
    padding: theme.spacing(2),
  },
}));
